import { useRouter } from "next/router"
import { useMemo } from "react"
import { type CookieValueTypes } from "cookies-next"
import { useTranslations } from "next-intl"
import { ACCOUNT_NUMBER_REGEXP } from "@/constants/regex"
import { getQuerylessPath } from "@/lib/routes"
import { DASHBOARD_ROOT_URL, dashboardRoutesPaths } from "@/routes/dashboard"
import { useCurrentNavigation } from "../../../hooks/useCurrentNavigation"
import { type TransformBreadcrumb } from "../types/TransformBreadcrumb"

const MAX_BREADCRUMBS = 3

const omittedPaths = [
    /logements/, // property
    /compteur/, // meter
    /modifier-mes-mensualites/, // update monthly billing amount
    /^\d+$/, // any path that is only a number, e.g. a property ID, meter ID
    /\d+\-\d+/, // any path that is a combination of numbers e.g consumption graphs
]

const omittedPortfolioPaths = [/espace-client/, ...omittedPaths]

const omittedSingleAccountPaths = [
    /comptes/, // accounts
    ACCOUNT_NUMBER_REGEXP, // account number
    ...omittedPaths,
]

type UseBreadcrumbsProps = {
    hasPortfolio: boolean
    transformBreadcrumb?: TransformBreadcrumb
    lastDashboardNavigationCookie?: CookieValueTypes
}

export function useBreadcrumbs({
    hasPortfolio,
    lastDashboardNavigationCookie,
    transformBreadcrumb,
}: UseBreadcrumbsProps) {
    const router = useRouter()
    const { accountNumber, propertyId } = useCurrentNavigation(lastDashboardNavigationCookie)
    const t = useTranslations("useBreadcrumbs")

    return useMemo(() => {
        const pathNames = getQuerylessPath(router.asPath).split("/").filter(Boolean)

        const pathsToOmit = hasPortfolio ? omittedPortfolioPaths : omittedSingleAccountPaths

        return pathNames
            .map((pathName) => {
                if (pathName === DASHBOARD_ROOT_URL) {
                    if (accountNumber && propertyId) {
                        return {
                            pathName,
                            href: dashboardRoutesPaths.property.resolve({
                                pathParams: { accountNumber, propertyId },
                            }),
                        }
                    }

                    return {
                        pathName,
                        href: dashboardRoutesPaths.root.resolve(),
                    }
                }

                return {
                    pathName,
                    href: `/${pathNames.slice(0, pathNames.indexOf(pathName) + 1).join("/")}`,
                }
            })
            .map(({ pathName, href }) => {
                if (ACCOUNT_NUMBER_REGEXP.test(pathName)) {
                    return {
                        href,
                        pathName,
                        text: pathName,
                    }
                }

                return {
                    href,
                    pathName,
                    text: t("breadcrumb", { pathName: pathName.replaceAll("-", "_") }), // next-intl doesn't support dashes
                }
            })
            .flatMap((breadcrumb, index) => {
                const transformedBreadcrumb = transformBreadcrumb?.(breadcrumb, pathNames, index)

                if (transformedBreadcrumb) {
                    return transformedBreadcrumb
                }

                if (pathsToOmit.some((omittedPath) => omittedPath.test(breadcrumb.pathName))) {
                    return []
                }

                return breadcrumb
            })
            .slice(-MAX_BREADCRUMBS)
    }, [router.asPath, hasPortfolio, accountNumber, propertyId, t, transformBreadcrumb])
}

useBreadcrumbs.messages = ["useBreadcrumbs"] as const
